import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="forgot-password-page">
      <div className="eyebrow">Account</div>
      <h1 className="h-display text-4xl mt-2">Forgot password.</h1>

      {!sent ? (
        <>
          <p className="text-sm text-[var(--ink-soft)] mt-2">
            Enter the email tied to your account. We'll send you a one-time link to choose a new password. The link expires in 1 hour.
          </p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-password-form">
            <div>
              <label className="eyebrow block mb-1.5">Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                autoComplete="email"
                className="w-full px-4 py-3 border border-[var(--line)] bg-[var(--paper-2)] text-sm rounded-md outline-none focus:border-[var(--ink)]"
                data-testid="forgot-password-email"
              />
            </div>
            <button
              disabled={busy}
              type="submit"
              className="btn-primary w-full justify-center disabled:bg-[var(--ink-muted)]"
              data-testid="forgot-password-submit"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        </>
      ) : (
        <div className="mt-8 border border-[var(--line)] bg-[var(--paper-2)] p-6 rounded-md" data-testid="forgot-password-sent">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 mt-0.5 text-[var(--terra)]" />
            <div>
              <div className="font-semibold">Check your inbox.</div>
              <p className="text-sm text-[var(--ink-soft)] mt-1">
                If <span className="font-mono">{email}</span> is registered with us, a reset link is on its way. It expires in 1 hour.
              </p>
              <p className="text-xs text-[var(--ink-muted)] mt-3 font-mono">
                Didn't get it? Check spam, or wait a minute before requesting another.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 text-sm text-[var(--ink-soft)]">
        <Link to="/login" className="inline-flex items-center gap-1 text-[var(--terra)] font-medium link-line" data-testid="back-to-login">
          <ArrowLeft className="h-3 w-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
